import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { FaPlus } from "react-icons/fa";

const FileInput = ({ id, accept, index, file, handleFileChange, handleRemoveFile }) => {
  const { t } = useTranslation();

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef();

  const allowedTypes = ["image/gif", "image/png", "image/jpeg"];
  const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (file) {
      if (typeof file === "string") {
        setPreview(file);
      } else if (file instanceof File) {
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
      }
    } else {
      setPreview(null);
    }
  }, [file]);

  const validateFile = (file) => {
    if (!allowedTypes.includes(file.type)) {
      alert(t("invalidFileType"));
      return false;
    }
    if (file.size > maxSizeInBytes) {
      alert(t("fileTooLarge"));
      return false;
    }
    return true;
  };

  const handleFile = (file) => {
    if (validateFile(file)) {
      const url = URL.createObjectURL(file);

      setPreview(url);

      handleFileChange(file, index);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemove = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    handleRemoveFile(index);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      className={`relative block my-3 rounded-xl overflow-hidden bg-sideBar border border-dashed cursor-pointer transition-colors min-h-[150px]
        ${isDragging ? "border-blue-500 bg-blue-50" : "border-white"}`}
    >
      {preview ? (
        <div className="relative h-full">
          <img src={preview} alt="preview" className="object-cover w-full h-full" />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            className="absolute grid w-6 h-6 text-xs text-white bg-red-500 rounded-full place-content-center top-2 right-2"
          >
            <FaPlus className="text-[10px] rotate-45" />
          </button>
        </div>
      ) : (
        <p className="text-[35px] absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          <FaPlus />
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        id={id}
        name={id}
        onChange={handleChange}
        className="hidden"
        accept={accept || ".gif, .png, .jpg, .jpeg"}
      />
    </div>
  );
};

export default FileInput;
